// Button and IconButton: machined keys, never pills. docs/operations/design-standard.md,
// "Controls". `title` is left out of the props on purpose: on a <button> it replaces the
// accessible name (web/CLAUDE.md).

import React from 'react';
import { CONTROL_MOTION, FOCUS, PRESS, STATE, STATE_ON_PRIMARY, TOUCH } from './motion';

export type ButtonVariant = 'filled' | 'tonal' | 'quiet' | 'danger' | 'danger-quiet' | 'danger-solid' | 'good' | 'info' | 'warn' | 'purple' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg';

type NativeButton = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'>;

export interface ButtonProps extends NativeButton {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** An icon before the label. */
  leading?: React.ReactNode;
}

/** A key that is on (aria-pressed): the accent ring of light, the kit's "this is chosen". */
const PRESSED = 'aria-pressed:text-on-surface aria-pressed:shadow-glow';

const BASE = `inline-flex min-w-0 select-none items-center justify-center whitespace-nowrap rounded-button text-[13px] leading-[18px] font-medium disabled:pointer-events-none disabled:!bg-surface-container disabled:!text-on-surface-disabled disabled:!shadow-none ${PRESSED} ${CONTROL_MOTION} ${PRESS} ${FOCUS} ${TOUCH}`;

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-7 gap-1 px-2.5',
  md: 'h-8 gap-1.5 px-3',
  // Shop-floor and kiosk screens, worked with gloves or at arm's length.
  lg: 'h-11 gap-2 px-4 text-sm',
};

// Tones are the category family (categoryColors.ts): a mark fill at 15% under ink text, written
// out literally because Tailwind only builds classes it can read in the source.
const VARIANT: Record<ButtonVariant, string> = {
  filled: `bg-primary font-semibold text-on-primary ${STATE_ON_PRIMARY}`,
  tonal: `bg-surface-container-highest text-on-surface shadow-key ${STATE}`,
  quiet: `bg-transparent text-on-surface-variant hover:text-on-surface ${STATE}`,
  danger: `bg-[color:oklch(0.7_0.16_25_/_0.15)] text-[color:oklch(0.83_0.094_25)] shadow-key ${STATE}`,
  // A row's own delete, beside many rows: red words, no fill, so a table is not a wall of red keys.
  'danger-quiet': `bg-transparent text-[color:oklch(0.83_0.094_25)] hover:text-red-200 ${STATE}`,
  // The one irreversible action on a surface (reject, destroy). Everything else is `danger`.
  'danger-solid': `bg-red-600 font-semibold text-white shadow-key ${STATE}`,
  // Meaning by colour, always beside a word that says the same: a legend the shop learned
  // (print Label gold, QR blue, Both green), a step that is a success, a caution to read first.
  good: `bg-[color:oklch(0.7_0.16_148_/_0.15)] text-[color:oklch(0.83_0.13_148)] shadow-key ${STATE}`,
  info: `bg-[color:oklch(0.7_0.16_254_/_0.15)] text-[color:oklch(0.83_0.086_254)] shadow-key ${STATE}`,
  warn: `bg-[color:oklch(0.7_0.143_94_/_0.15)] text-[color:oklch(0.83_0.13_94)] shadow-key ${STATE}`,
  // Shipping's colour (packing on the calendar and dispatch, shipped on order pills): Pack & Ship, Fulfillment.
  purple: `bg-[color:oklch(0.7_0.16_300_/_0.15)] text-[color:oklch(0.83_0.099_300)] shadow-key ${STATE}`,
  // A record's own action in the accent's tint (an asset's Assign barcode), at the tones' strength.
  accent: `bg-primary/15 text-primary-ink shadow-key ${STATE}`,
};

export const buttonClass = (variant: ButtonVariant = 'tonal', size: ButtonSize = 'md'): string =>
  `${BASE} ${SIZE[size]} ${VARIANT[variant]}`;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'tonal', size = 'md', leading, className = '', type = 'button', children, ...rest }, ref) => (
    <button ref={ref} type={type} className={`${buttonClass(variant, size)} ${className}`} {...rest}>
      {leading ? <span className="-ml-0.5 inline-flex flex-none">{leading}</span> : null}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

export interface IconButtonProps extends Omit<NativeButton, 'aria-label'> {
  /** What pressing it does, in words. Becomes the accessible name. */
  label: string;
  size?: ButtonSize;
}

const ICON_SIZE: Record<ButtonSize, string> = { sm: 'h-7 w-7', md: 'h-8 w-8', lg: 'h-11 w-11' };

/** Transparent and padded: the icon is the affordance. A ring only on keyboard focus. */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, size = 'md', className = '', type = 'button', children, ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      className={`inline-flex flex-none select-none items-center justify-center rounded-control bg-transparent text-on-surface-variant hover:text-on-surface disabled:pointer-events-none disabled:!bg-surface-container disabled:!text-on-surface-disabled disabled:!shadow-none ${PRESSED} ${ICON_SIZE[size]} ${STATE} ${CONTROL_MOTION} ${PRESS} ${FOCUS} ${TOUCH} ${className}`}
      {...rest}
    >
      {children}
    </button>
  ),
);
IconButton.displayName = 'IconButton';

export default Button;
